/**
 * Labiarge localization modal. Geo detection + language/currency switcher.
 *
 * Clones the <template> emitted by snippets/labiarge-localization-modal.liquid
 * into <body>, then on a visitor's first visit asks Shopify where they are:
 *
 *   GET {root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true
 *   -> { detected_values: { country: { handle: "ES", name: "Spain" } }, suggestions: [...] }
 *
 * `detected_values.country.handle` is geo-IP derived, which is the whole point:
 * Shopify's own automatic redirection keys off the *browser* language, so a
 * Turkey visitor running an English browser never sees Turkish. We map country
 * to language ourselves (data-country-language-map, default = primary language)
 * and apply it, then open the modal so the choice is visible and reversible.
 *
 * Guarantees:
 *   - Auto-apply runs at most once per browser, ever (AUTO_KEY). A locale that
 *     doesn't stick can therefore never turn into a redirect loop.
 *   - Once the visitor has seen the modal (ACK_KEY) it never opens by itself
 *     again; the header control still opens it on demand.
 *   - Never auto-applies inside the theme editor. A redirect there would fight
 *     the customizer. Manual opening still works so the merchant can preview it.
 *   - Every storage read/write is guarded: Safari private mode throws on
 *     setItem, and that must not take the modal down with it.
 */

const TEMPLATE_ID = 'labiarge-localization-modal-template';
const OPEN_EVENT = 'labiarge:localization:open';

/** Visitor has seen the modal. Stop opening it unprompted. */
const ACK_KEY = 'labiarge:localization:ack';
/** Geo auto-apply has already fired once on this browser. */
const AUTO_KEY = 'labiarge:localization:auto';
/** Survives the auto-apply reload so the modal can report what happened. */
const PENDING_KEY = 'labiarge:localization:pending';
/** Chosen *display* currency. A client-side preference, see below. */
const CURRENCY_KEY = 'labiarge:localization:currency';
const CURRENCY_CHANGE_EVENT = 'labiarge:currency:change';

/**
 * Kept in step with STALE_AFTER_DAYS in labiarge-fx.js by hand. The two modules
 * read the same rates blob but stay decoupled: importing across our own assets
 * would resolve without the `?v=` cache key Shopify puts on asset URLs, so a
 * redeploy could pair a fresh module with a stale one.
 */
const STALE_AFTER_DAYS = 7;

/** @type {HTMLDialogElement | null} */
let dialog = null;

/**
 * @param {Storage} store
 * @param {string} key
 * @returns {string | null}
 */
function read(store, key) {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} store
 * @param {string} key
 * @param {string} value
 */
function write(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    /* private mode. The modal degrades to "shows every visit", not to broken */
  }
}

/**
 * @param {Storage} store
 * @param {string} key
 */
function drop(store, key) {
  try {
    store.removeItem(key);
  } catch {
    /* see write() */
  }
}

/** Clone the template into <body> once and wire it up. */
function mount() {
  if (dialog) return dialog;

  const template = document.getElementById(TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return null;

  document.body.append(template.content.cloneNode(true));

  const el = document.querySelector('[data-labiarge-loc]');
  if (!(el instanceof HTMLDialogElement)) return null;
  dialog = el;

  const form = dialog.querySelector('form');

  // There is no close button; Escape is the only dismissal that is not Continue,
  // and it counts as accepting the pre-selected values.
  dialog.addEventListener('close', () => {
    acknowledge();
    setScrollLock(false);
  });

  // Restore the stored currency over the server-rendered default, then keep the
  // note in step with it.
  const stored = read(localStorage, CURRENCY_KEY);
  if (stored) {
    const match = dialog.querySelector(`input[name="labiarge_currency"][value="${CSS.escape(stored)}"]`);
    if (match instanceof HTMLInputElement) match.checked = true;
  }
  updateCurrencyNote();
  dialog.addEventListener('change', onSelectionChange);

  // Seed the cache with what the server already gave us, so switching back to
  // the rendered language is instant and costs no request.
  const serverLanguage = dialog.dataset.currentLanguage;
  if (serverLanguage) stringCache.set(serverLanguage, readStrings(dialog));

  // No rates, or rates too old to quote. Drop the picker rather than offer a
  // currency whose figure we would then refuse to render next to prices.
  if (!loadRates()) {
    dialog.querySelector('[data-labiarge-loc-currency-group]')?.remove();
  }

  form?.addEventListener('submit', (event) => {
    // Currency is ours, not Shopify's. Never post it to /localization.
    for (const input of form.querySelectorAll('input[name="labiarge_currency"]')) {
      input.disabled = true;
    }

    // Nothing actually changed. Don't burn a page load on a no-op round trip.
    if (!isChanged(form)) {
      event.preventDefault();
      for (const input of form.querySelectorAll('input[name="labiarge_currency"]')) {
        input.disabled = false;
      }
      close();
      return;
    }
    acknowledge();
    setReturnTo(form);
  });

  return dialog;
}

/**
 * True when submitting would actually change the Shopify-side context, i.e.
 * language. Currency is applied live on change and never posted, so a
 * currency-only edit must not trigger a page load.
 */
function isChanged(form) {
  const language = form.querySelector('input[name="language_code"]:checked');
  return language instanceof HTMLInputElement && language.value !== dialog?.dataset.currentLanguage;
}

/**
 * Point Shopify back at the page the visitor is on. It re-prefixes the locale
 * itself, so the un-prefixed current path is the right value to hand over.
 * @param {HTMLFormElement} form
 */
function setReturnTo(form) {
  let input = form.querySelector('input[name="return_to"]');
  if (!(input instanceof HTMLInputElement)) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'return_to';
    form.append(input);
  }
  input.value = window.location.pathname + window.location.search;
}

function acknowledge() {
  write(localStorage, ACK_KEY, dialog?.dataset.currentLanguage || '1');
}

/**
 * Horizon v4 scrolls `div.page-wrapper`, not the document, `<html>` and
 * `<body>` are already `overflow: hidden`, so the usual `html { overflow:
 * hidden }` lock is a no-op and the page keeps scrolling behind the modal.
 * `html[scroll-lock] .page-wrapper { overflow: hidden }` in assets/base.css is
 * the theme's own mechanism; setting the attribute is enough.
 *
 * Deliberately not `lockScroll()` from `@theme/utilities`: that owner-set API
 * exists so several dialogs can hold the lock at once, which cannot happen
 * here: showModal() puts this dialog in the top layer, so nothing behind it
 * is clickable and no second owner can appear. The attribute is the smaller
 * coupling, and if base.css ever renames it we lose the lock, not the modal.
 * @param {boolean} locked
 */
function setScrollLock(locked) {
  document.documentElement.toggleAttribute('scroll-lock', locked);
}

function close() {
  if (dialog?.open) dialog.close();
}

/**
 * Which copy the intro is showing. Remembered so the intro can be re-rendered
 * in another language without the caller having to supply it again.
 * @type {{mode: 'generic' | 'detected', country?: {handle: string, name: string}}}
 */
let introState = { mode: 'generic' };

/**
 * The detected country, named in the language the panel is currently speaking.
 *
 * Shopify does not localize this: browsing_context_suggestions.json returns
 * "United States" whether it is asked through / or through /tr, so an earlier
 * attempt to re-fetch it per locale bought nothing but a request.
 * Intl.DisplayNames does the job offline and for every locale at once.
 */
function countryLabel() {
  const country = introState.country;
  if (!country) return '';

  const language = dialog?.lang || dialog?.dataset.currentLanguage || 'en';
  try {
    const label = new Intl.DisplayNames([language], { type: 'region' }).of(country.handle);
    if (label && label !== country.handle) return label;
  } catch {
    /* unsupported locale or code, fall back to what Shopify gave us */
  }
  return country.name || '';
}

function renderIntro() {
  const intro = dialog?.querySelector('[data-labiarge-loc-intro]');
  if (!(intro instanceof HTMLElement)) return;

  const { introGeneric = '', introDetected = '', introDetectedFallback = '' } = dialog.dataset;
  if (introState.mode === 'detected') {
    const label = countryLabel();
    intro.textContent = label
      ? introDetected.replace('[country]', label)
      : introDetectedFallback || introGeneric;
  } else {
    intro.textContent = introGeneric;
  }
}

/**
 * @param {'generic' | 'detected'} [mode] - 'detected' frames the copy as "we
 *   chose this for you"; 'generic' (the header control) just offers the choice.
 * @param {{handle: string, name: string}} [country] - Named in the 'detected'
 *   copy when detection returned one.
 */
function open(mode = 'generic', country) {
  const el = mount();
  if (!el || el.open) return;

  introState = { mode, country };
  renderIntro();

  el.showModal();
  setScrollLock(true);
}

/** @returns {Promise<{handle: string, name: string} | null>} */
async function detectCountry() {
  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`;

  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const data = await response.json();
    const country = data?.detected_values?.country;
    return country?.handle ? country : null;
  } catch {
    return null;
  }
}

/**
 * Look a country code up in one of the `AA:xx,BB:yy` maps the Liquid renders.
 * @param {string} map
 * @param {string | undefined} countryCode
 * @param {string} fallback
 * @returns {string}
 */
function lookup(map, countryCode, fallback) {
  if (!countryCode) return fallback;
  for (const pair of map.split(',')) {
    const [country, value] = pair.split(':');
    if (country?.trim().toUpperCase() === countryCode.toUpperCase()) return value?.trim() || fallback;
  }
  return fallback;
}

/**
 * Resolve the language a country should be served in.
 * @param {string | undefined} countryCode
 */
function languageFor(countryCode) {
  const fallback = dialog?.dataset.primaryLanguage || '';
  return lookup(dialog?.dataset.countryLanguageMap || '', countryCode, fallback);
}

/**
 * Resolve the display currency a country should read prices in.
 * @param {string | undefined} countryCode
 */
function currencyFor(countryCode) {
  const fallback = dialog?.dataset.defaultCurrency || dialog?.dataset.shopCurrency || '';
  return lookup(dialog?.dataset.countryCurrencyMap || '', countryCode, fallback);
}

/** The server-rendered rates blob, or null when absent or too old. */
function loadRates() {
  const node = document.getElementById('labiarge-fx-rates');
  if (!node) return null;
  try {
    const parsed = JSON.parse(node.textContent || '');
    if (!parsed?.per_unit || !parsed?.updated_at) return null;
    const age = (Date.now() - Date.parse(parsed.updated_at)) / 86_400_000;
    return Number.isFinite(age) && age <= STALE_AFTER_DAYS ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Reveal the note belonging to the selected currency.
 *
 * Every note is rendered server-side and the set is stacked in a single grid
 * cell, so this only flips which one is visible. The tallest fixes the height,
 * which is what stops the panel growing (and re-centring, and jumping under the
 * cursor) when the visitor switches from TRY to a reference currency.
 */
function updateCurrencyNote() {
  const selected = dialog?.querySelector('input[name="labiarge_currency"]:checked');
  const currency = selected instanceof HTMLInputElement ? selected.value : '';

  for (const note of dialog?.querySelectorAll('.labiarge-loc__note[data-currency]') || []) {
    note.toggleAttribute('data-active', note.dataset.currency === currency);
  }
}

/* ---------------------------------------------------------------------------
 * Live language preview
 *
 * The panel is server-rendered in the *active* locale, so picking Türkçe used to
 * leave every label around it in English until the visitor pressed Continue.
 * Someone who does not read English then has to confirm a currency notice they
 * cannot read, which is precisely the notice that says what they will be
 * charged. And the page behind is blurred, so there is no context to fall back
 * on either.
 *
 * Rather than duplicate the copy into a bilingual bundle (two places to keep in
 * step, and it would break Translate & Adapt), fetch the same modal from the
 * other locale's URL and lift its strings out. The locale files stay the single
 * source of truth and it scales to a third language for free.
 *
 * Cost is one page fetch, on interaction only, cached per language for the
 * session. Roughly 40 KB gzipped, off the critical path entirely.
 * ------------------------------------------------------------------------- */

/** @type {Map<string, object>} language -> the modal's strings in it */
const stringCache = new Map();
/** Guards against a slow response for a language the visitor already left. */
let localizeToken = 0;

/**
 * Read the translatable strings out of a rendered modal.
 * @param {ParentNode} root - a dialog, or a <template>'s content fragment
 */
function readStrings(root) {
  const host = root.querySelector('[data-labiarge-loc]') || root;

  // Structured rather than one blob of text: a reference-currency note is a
  // sentence plus a rate row, and copying innerHTML across a fetch boundary is
  // a habit worth not forming.
  const notes = {};
  for (const note of root.querySelectorAll('.labiarge-loc__note[data-currency]')) {
    notes[note.dataset.currency] = {
      text: note.querySelector('.labiarge-loc__note-text')?.textContent.trim() || '',
      rateValue: note.querySelector('.labiarge-loc__rate-value')?.textContent.trim() || '',
      rateDate: note.querySelector('.labiarge-loc__rate-date')?.textContent.trim() || '',
    };
  }

  return {
    title: root.querySelector('.labiarge-loc__title')?.textContent.trim() || '',
    legends: Array.from(root.querySelectorAll('.labiarge-loc__legend'), (el) => el.textContent.trim()),
    submit: root.querySelector('.labiarge-loc__submit')?.textContent.trim() || '',
    notes,
    introGeneric: host.dataset?.introGeneric || '',
    introDetected: host.dataset?.introDetected || '',
    introDetectedFallback: host.dataset?.introDetectedFallback || '',
  };
}

/** Paint a set of strings onto the open dialog. */
function applyStrings(strings) {
  if (!strings || !dialog) return;

  const title = dialog.querySelector('.labiarge-loc__title');
  if (title && strings.title) title.textContent = strings.title;

  dialog.querySelectorAll('.labiarge-loc__legend').forEach((el, index) => {
    if (strings.legends[index]) el.textContent = strings.legends[index];
  });

  const submit = dialog.querySelector('.labiarge-loc__submit');
  if (submit && strings.submit) submit.textContent = strings.submit;

  for (const note of dialog.querySelectorAll('.labiarge-loc__note[data-currency]')) {
    const parts = strings.notes[note.dataset.currency];
    if (!parts) continue;
    const set = (selector, value) => {
      const el = note.querySelector(selector);
      if (el && value) el.textContent = value;
    };
    set('.labiarge-loc__note-text', parts.text);
    set('.labiarge-loc__rate-value', parts.rateValue);
    set('.labiarge-loc__rate-date', parts.rateDate);
  }

  dialog.dataset.introGeneric = strings.introGeneric;
  dialog.dataset.introDetected = strings.introDetected;
  dialog.dataset.introDetectedFallback = strings.introDetectedFallback;
  renderIntro();
}

/**
 * Fetch the modal as the given language renders it.
 *
 * Locale URLs are subfolders with the primary language at the root, which is how
 * this store's languages are attached to the domain (CLAUDE.md, "Store, themes,
 * and access"). If that ever changes to per-domain locales, this is the line to
 * change.
 *
 * @param {string} language
 * @returns {Promise<object | null>}
 */
async function fetchStrings(language, path) {
  if (stringCache.has(language)) return stringCache.get(language);

  try {
    const response = await fetch(path, { headers: { Accept: 'text/html' } });
    if (!response.ok) return null;
    const template = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .getElementById(TEMPLATE_ID);
    if (!(template instanceof HTMLTemplateElement)) return null;

    const strings = readStrings(template.content);
    stringCache.set(language, strings);
    return strings;
  } catch {
    return null;
  }
}

/**
 * Re-skin the panel in the selected language.
 * @param {string} language
 */
async function localize(language) {
  if (!dialog) return;

  const token = ++localizeToken;
  // Announce the panel in the language it is about to speak.
  dialog.lang = language;

  const primary = dialog.dataset.primaryLanguage;
  const root = language === primary ? '/' : `/${language}/`;

  const cached = stringCache.get(language);
  if (cached) {
    applyStrings(cached);
    return;
  }

  dialog.setAttribute('aria-busy', 'true');
  const strings = await fetchStrings(language, root);

  // A newer selection won the race; its own call owns the panel now.
  if (token !== localizeToken) return;

  dialog.removeAttribute('aria-busy');
  // On failure the panel stays in the language it was rendered in, and Continue
  // still applies the choice. Worse copy, never a broken modal. Either way the
  // intro re-renders, because the country label follows dialog.lang.
  if (strings) applyStrings(strings);
  else renderIntro();
}

/**
 * One delegated handler for both chip groups, since the dialog owns them both.
 */
function onSelectionChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;

  if (input.name === 'labiarge_currency') {
    write(localStorage, CURRENCY_KEY, input.value);
    updateCurrencyNote();
    document.dispatchEvent(new CustomEvent(CURRENCY_CHANGE_EVENT));
    return;
  }

  if (input.name === 'language_code') {
    // Preview only. The language is not actually applied until Continue, which
    // is what reloads the page and the rest of the storefront with it.
    localize(input.value);
  }
}

/**
 * Submit the localization form with a new language.
 * @param {string} language
 * @returns {boolean} whether the submit was actually issued
 */
function autoApply(language) {
  const form = dialog?.querySelector('form');
  const input = form?.querySelector(`input[name="language_code"][value="${CSS.escape(language)}"]`);
  if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return false;

  input.checked = true;
  setReturnTo(form);
  write(localStorage, AUTO_KEY, '1');
  write(sessionStorage, PENDING_KEY, '1');
  form.submit();
  return true;
}

async function init() {
  if (!mount()) return;

  // Landed here from our own auto-apply. Report the result, don't re-detect.
  if (read(sessionStorage, PENDING_KEY)) {
    drop(sessionStorage, PENDING_KEY);
    open('detected');
    return;
  }

  if (window.Shopify?.designMode) return;
  if (read(localStorage, ACK_KEY)) return;

  const country = await detectCountry();

  // Seed the display currency from the detected country the first time only -
  // after that the visitor's own choice stands, even if they travel.
  if (!read(localStorage, CURRENCY_KEY)) {
    const currency = currencyFor(country?.handle);
    if (currency) {
      write(localStorage, CURRENCY_KEY, currency);
      const match = dialog?.querySelector(`input[name="labiarge_currency"][value="${CSS.escape(currency)}"]`);
      if (match instanceof HTMLInputElement) match.checked = true;
      updateCurrencyNote();
      document.dispatchEvent(new CustomEvent(CURRENCY_CHANGE_EVENT));
    }
  }

  const desired = languageFor(country?.handle);
  const current = dialog?.dataset.currentLanguage;

  if (desired && desired !== current && !read(localStorage, AUTO_KEY) && autoApply(desired)) return;

  open('detected', country || undefined);
}

document.addEventListener(OPEN_EVENT, () => open('generic'));

init();
